import openCompose from './openCompose';
import trySaveAndCloseCompose from './trySaveAndCloseCompose';
import { getStore } from '../store/composeStore';
import ComposeTarget from '../store/schema/ComposeTarget';
import { ComposeOperation } from '../store/schema/ComposeViewState';
import filterNdrSubmittedRecipients from '../utils/filterNdrSubmittedRecipients';
import { HTML_BODY_TYPE } from '../utils/getDefaultBodyType';
import { AppendOnSend } from 'owa-addins-core';
import isEncryptionEnabled from 'owa-encryption-common/lib/utils/isEncryptionEnabled';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getExtendedProperty } from 'owa-folders-common';
import { ClientItem, mailStore } from 'owa-mail-store';
import getItem from 'owa-mail-store/lib/services/getItem';
import checkIfResponseSuccess from 'owa-mail-store/lib/services/utils/checkIfResponseSuccess';
import SmimeType from 'owa-smime-adapter/lib/store/schema/SmimeType';
import { getComposeItemResponseShape } from 'owa-mail-store/lib/utils/getItemResponseShape';
import isMeetingRequest from 'owa-meeting-message/lib/utils/isMeetingRequest';
import { isDeepLink } from 'owa-popout';
import BodyType from 'owa-service/lib/contract/BodyType';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import Message from 'owa-service/lib/contract/Message';
import SingleResponseMessage from 'owa-service/lib/contract/SingleResponseMessage';
import { lazySetSmimePropertiesInDraftMessage } from 'owa-smime';
import doesItemNeedToBeFetchedAgain from 'owa-smime/lib/utils/doesItemNeedToBeFetchedAgain';
import getStampedHeaderForSmime from 'owa-smime/lib/utils/getStampedHeaderForSmime';
import isSMIMEItem from 'owa-smime/lib/utils/isSMIMEItem';
import { isSxSDisplayed } from 'owa-sxs-store';
import { getStore as getTabStore, lazyActivateTab, TabType } from 'owa-tab-store';
import isTabViewEnabled from 'owa-tab-store/lib/utils/isTabViewEnabled';
import { trace } from 'owa-trace';
import { action } from 'satcheljs/lib/legacy';
import createSmimeDefaultViewState from 'owa-smime/lib/utils/createSmimeDefaultViewState';

/**
 * This function gets the draft message response from server.
 * For S/MIME clear/opaque signed mail we need to stamp X-OWA-SmimeInstalled header
 * in request api, in order to get the p7m file as attachment in draft message response
 * so that we can decode it and load it to compose.
 */
async function getItemResponse(itemId: string): Promise<SingleResponseMessage[]> {
    const cachedItem = mailStore.items.get(itemId);
    /**
     * If item is already present in mailStore, we can determine whether
     * it is an S/MIME item and thus get the appropriate S/MIME headers
     * for it.
     */
    const headers = await getStampedHeaderForSmime(cachedItem);
    // Pass null as body type to use original body type of the draft
    const shape = getComposeItemResponseShape(null /*bodyType*/);
    const response = await getItem([itemId], shape, 'MailCompose', null, { headers });
    if (isFeatureEnabled('cmp-smime') && checkIfResponseSuccess(response[0])) {
        const responseMessage = response[0] as ItemInfoResponseMessage;
        const message = responseMessage.Items[0] as Message;

        /**
         * Item might need to be fetched again to get the p7m dataPackage in case this was a clear/opaque signed message,
         * but ItemClass was not known before the first fetch.
         * eg: in case of popout and deeplink
         */
        if (doesItemNeedToBeFetchedAgain(message as ClientItem)) {
            const headers = await getStampedHeaderForSmime(message);
            return getItem([itemId], shape, 'MailCompose', null /* requestServerVersion */, {
                headers,
            });
        }
    }

    return response;
}

/**
 * This function merges the decoded S/MIME properties
 * to draftMessage and returns smimeType of the decoded
 * message which is used to initialize composeViewState
 *
 * @param message S/MIME message to decode.
 * @return SmimeType of the decoded message.
 */
function processSmimeProperties(message: Message): Promise<SmimeType> {
    return lazySetSmimePropertiesInDraftMessage
        .import()
        .then(setSmimePropertiesInDraftMessage => setSmimePropertiesInDraftMessage(message));
}

export default action('loadDraftToCompose')(async function loadDraftToCompose(
    itemId: string,
    openTarget: ComposeTarget = ComposeTarget.SecondaryTab,
    isNdrResend?: boolean
) {
    const openInSxS = isSxSDisplayed() && openTarget !== ComposeTarget.Popout;
    const openInTab =
        !isDeepLink() && isTabViewEnabled() && openTarget === ComposeTarget.SecondaryTab;
    const store = getStore();

    if (!openInTab && !openInSxS) {
        if (openTarget == ComposeTarget.SecondaryTab) {
            // Can't open in tab, fallback to primary reading pane
            openTarget = ComposeTarget.PrimaryReadingPane;
        }

        await trySaveAndCloseCompose(null /*viewState*/);

        if (store.primaryComposeId) {
            return;
        }
    } else if (openInTab) {
        // Check if this draft is already opened in tab
        const tabStore = getTabStore();
        for (const tab of tabStore.tabs) {
            if (tab.type === TabType.Compose) {
                const viewState = store.viewStates.get(tab.data);

                if (viewState.itemId && viewState.itemId.Id == itemId) {
                    lazyActivateTab.importAndExecute(tab);
                    return;
                }
            }
        }
    }

    if (openInSxS) {
        openTarget = ComposeTarget.SxS;
    }

    try {
        const response = await getItemResponse(itemId);
        if (checkIfResponseSuccess(response[0])) {
            const responseMessage = response[0] as ItemInfoResponseMessage;
            let message = responseMessage.Items[0] as Message;
            let smimeType: SmimeType = SmimeType.None;
            if (isFeatureEnabled('cmp-smime') && isSMIMEItem(message)) {
                smimeType = await processSmimeProperties(message);
            }
            const bodyType: BodyType = message.Body.BodyType == HTML_BODY_TYPE ? 'HTML' : 'Text';
            let appendOnSend: AppendOnSend[] = getExtendedProperty(message, 'AppendOnSend');
            appendOnSend = !!appendOnSend ? appendOnSend : []; // if no value, initialize to empty list
            const isInlineCompose = false;
            const conversationId = !!message.ConversationId ? message.ConversationId.Id : undefined;
            const composeInitProps = {
                appendOnSend: appendOnSend,
                operation: ComposeOperation.EditDraft,
                bodyType: bodyType,
                itemId: message.ItemId,
                subject: message.Subject,
                quotedBody: message.Body.Value,
                to:
                    isNdrResend && message.ToRecipients
                        ? filterNdrSubmittedRecipients(message.ToRecipients)
                        : message.ToRecipients,
                cc: message.CcRecipients,
                bcc: message.BccRecipients,
                attachments: message.Attachments,
                importance: message.Importance,
                isReadReceiptRequested: message.IsReadReceiptRequested,
                isDeliveryReceiptRequested: message.IsDeliveryReceiptRequested,
                preferAsyncSend: true,
                from: message.From != null ? message.From.Mailbox : null,
                pendingSocialActivityTags: message.PendingSocialActivityTagIds,
                IRMData: isEncryptionEnabled() ? message.RightsManagementLicenseData : null,
                meetingRequestItem:
                    isMeetingRequest(message.ItemClass) != null ? { ...message } : null,
                sensitivity: message.Sensitivity,
                isInlineCompose: isInlineCompose,
                conversationId: conversationId,
                // TOD0: Respect S/MIME admin/user settings in draft WI:45046
                smimeViewState: createSmimeDefaultViewState(smimeType),
            };

            await openCompose(composeInitProps, openTarget, null);
        }
    } catch (error) {
        // TODO: handle error. Add S/MIME Draft infobars
        trace.info(`[loadDraftToCompose] ${error.message}`);
    }
});
